/**
 * Job Description Editor Modal
 * Requirements: 9.5
 * Tabs: Job Description (text editor) and Job Tags (tag chips)
 */
import { useState, useEffect, useRef } from 'react';
import { X, Save, Loader2, Plus, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

type TabId = 'description' | 'tags';

interface JobDescriptionEditorProps {
  jobid: string;
  initialContent: string;
  initialTags?: string[];
  initialTitle?: string;
  initialCompany?: string;
  initialLocation?: string;
  initialSalary?: string;
  initialReqid?: string;
  initialSuperTags?: string[];
  tagsGenerating?: boolean;
  onClose: () => void;
  onSave: () => void;
  onRegenerateTags?: () => void;
}

export function JobDescriptionEditor({
  jobid,
  initialContent,
  initialTags = [],
  initialTitle = '',
  initialCompany = '',
  initialLocation = '',
  initialSalary = '',
  initialReqid = '',
  initialSuperTags = [],
  tagsGenerating = false,
  onClose,
  onSave,
  onRegenerateTags,
}: JobDescriptionEditorProps) {
  const [activeTab, setActiveTab] = useState<TabId>('description');
  const [content, setContent] = useState(initialContent);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [title, setTitle] = useState(initialTitle);
  const [company, setCompany] = useState(initialCompany);
  const [location, setLocation] = useState(initialLocation);
  const [salary, setSalary] = useState(initialSalary);
  const [reqid, setReqid] = useState(initialReqid);
  const [superTags, setSuperTags] = useState<string[]>(initialSuperTags);
  const [newTag, setNewTag] = useState('');
  const [newSuperTag, setNewSuperTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const superTagInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // Track whether user has manually modified tags (add/remove) to avoid overwriting their changes
  const tagsUserModified = useRef(false);
  const superTagsUserModified = useRef(false);

  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  useEffect(() => { setTitle(initialTitle); }, [initialTitle]);
  useEffect(() => { setCompany(initialCompany); }, [initialCompany]);
  useEffect(() => { setLocation(initialLocation); }, [initialLocation]);
  useEffect(() => { setSalary(initialSalary); }, [initialSalary]);
  useEffect(() => { setReqid(initialReqid); }, [initialReqid]);

  // Sync tags from parent (e.g., background tag generation) only if user hasn't manually edited them
  useEffect(() => {
    if (!tagsUserModified.current) {
      setTags(initialTags);
    }
  }, [initialTags]);

  // Same sync guard for tag categories (super_tags), regenerated alongside tags
  useEffect(() => {
    if (!superTagsUserModified.current) {
      setSuperTags(initialSuperTags);
    }
  }, [initialSuperTags]);

  useEscapeClose(handleCancel);
  useFocusTrap(modalRef, true);

  const descriptionChanged = content !== initialContent;
  const tagsChanged = JSON.stringify(tags) !== JSON.stringify(initialTags);
  const superTagsChanged = JSON.stringify(superTags) !== JSON.stringify(initialSuperTags);
  const titleChanged = title !== initialTitle;
  const companyChanged = company !== initialCompany;
  const locationChanged = location !== initialLocation;
  const salaryChanged = salary !== initialSalary;
  const reqidChanged = reqid !== initialReqid;
  const detailsChanged = titleChanged || companyChanged || locationChanged || salaryChanged || reqidChanged;
  const hasChanges = descriptionChanged || tagsChanged || superTagsChanged || detailsChanged;
  const canSave = hasChanges && title.trim() !== '' && company.trim() !== '';

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const detailUpdates: {
        jobdesc?: string;
        jobtitle?: string;
        jobcompany?: string;
        joblocation?: string;
        jobsalary?: string;
        jobreqid?: string;
        super_tags?: string[];
      } = {};
      if (descriptionChanged) detailUpdates.jobdesc = content;
      if (titleChanged) detailUpdates.jobtitle = title.trim();
      if (companyChanged) detailUpdates.jobcompany = company.trim();
      if (locationChanged) detailUpdates.joblocation = location;
      if (salaryChanged) detailUpdates.jobsalary = salary;
      if (reqidChanged) detailUpdates.jobreqid = reqid;
      if (superTagsChanged) detailUpdates.super_tags = superTags;

      if (Object.keys(detailUpdates).length > 0) {
        await api.updateJobDetails(jobid, detailUpdates);
      }
      if (tagsChanged) {
        await api.updateJobTags(jobid, tags);
      }
      onSave();
      onClose();
    } catch (err) {
      setError('Failed to save changes');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    // Property 22: Cancel should not persist changes
    onClose();
  }

  function addTag() {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      tagsUserModified.current = true;
      setTags([...tags, trimmed]);
      setNewTag('');
      tagInputRef.current?.focus();
    }
  }

  function removeTag(index: number) {
    tagsUserModified.current = true;
    setTags(tags.filter((_, i) => i !== index));
  }

  function handleTagKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  }

  function addSuperTag() {
    const trimmed = newSuperTag.trim();
    if (trimmed && superTags.length < 5 &&
        !superTags.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      superTagsUserModified.current = true;
      setSuperTags([...superTags, trimmed]);
      setNewSuperTag('');
      superTagInputRef.current?.focus();
    }
  }

  function removeSuperTag(index: number) {
    superTagsUserModified.current = true;
    setSuperTags(superTags.filter((_, i) => i !== index));
  }

  function handleSuperTagKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSuperTag();
    }
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'description', label: 'Job Description' },
    { id: 'tags', label: tagsGenerating ? `Job Tags (${tags.length}) ...` : `Job Tags (${tags.length})` },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Edit Job Details">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Edit Job Details</h2>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Job Details (always visible) */}
        <div className="p-4 border-b bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-job-title" className="block text-xs font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-job-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Senior Software Engineer"
            />
          </div>
          <div>
            <label htmlFor="edit-job-company" className="block text-xs font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-job-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Acme Inc."
            />
          </div>
          <div>
            <label htmlFor="edit-job-location" className="block text-xs font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              id="edit-job-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Remote, San Francisco, CA"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="edit-job-salary" className="block text-xs font-medium text-gray-700 mb-1">
                Salary
              </label>
              <input
                id="edit-job-salary"
                type="text"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., $150k-$180k"
              />
            </div>
            <div className="w-40">
              <label htmlFor="edit-job-reqid" className="block text-xs font-medium text-gray-700 mb-1">
                Req ID
              </label>
              <input
                id="edit-job-reqid"
                type="text"
                value={reqid}
                onChange={(e) => setReqid(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., 4380290818"
              />
            </div>
          </div>
        </div>

        {/* Tab Control */}
        <div className="flex border-b px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {activeTab === 'description' && (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-96 p-3 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter job description..."
            />
          )}

          {activeTab === 'tags' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                  Tags help the AI match your resume to this job. They are auto-generated from the job description, but you can add, edit, or remove them.
                </p>
                {onRegenerateTags && (
                  <button
                    onClick={() => {
                      tagsUserModified.current = false;
                      onRegenerateTags();
                    }}
                    disabled={tagsGenerating}
                    className="flex items-center gap-1.5 ml-4 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${tagsGenerating ? 'animate-spin' : ''}`} />
                    Regenerate Job Tags
                  </button>
                )}
              </div>

              {/* Background tag generation indicator */}
              {tagsGenerating && (
                <div role="status" className="flex items-center gap-2 mb-4 p-2.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                  <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  <span>Generating tags from job description...</span>
                </div>
              )}

              {/* Tag Categories (super_tags) — high-level skill groupings */}
              <div className="mb-5 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-sm font-semibold text-gray-800">Tag Categories</h3>
                  <span className="text-xs text-gray-400">{superTags.length}/5</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  High-level themes for this job (e.g. "Cloud &amp; Infrastructure", "AI / ML"). Your generated resume groups skills under these headers. Auto-generated from the job description &mdash; edit if you like.
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    ref={superTagInputRef}
                    type="text"
                    value={newSuperTag}
                    onChange={(e) => setNewSuperTag(e.target.value)}
                    onKeyDown={handleSuperTagKeyDown}
                    disabled={superTags.length >= 5}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    placeholder={superTags.length >= 5 ? 'Maximum 5 categories' : 'Add a category...'}
                  />
                  <button
                    onClick={addSuperTag}
                    disabled={!newSuperTag.trim() || superTags.length >= 5}
                    className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </button>
                </div>
                {superTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {superTags.map((st, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-50 text-primary-800 border border-primary-200 rounded-full text-sm font-medium"
                      >
                        {st}
                        <button
                          onClick={() => removeSuperTag(index)}
                          className="ml-0.5 p-0.5 hover:bg-primary-100 rounded-full"
                          title="Remove category"
                          aria-label={`Remove category ${st}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No categories yet. They'll be generated with the job tags, or add your own above.</p>
                )}
              </div>

              {/* Add tag input */}
              <div className="flex gap-2 mb-4">
                <input
                  ref={tagInputRef}
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Add a tag..."
                />
                <button
                  onClick={addTag}
                  disabled={!newTag.trim()}
                  className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </button>
              </div>

              {/* Tag chips */}
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(index)}
                        className="ml-0.5 p-0.5 hover:bg-gray-200 rounded-full"
                        title="Remove tag"
                        aria-label={`Remove tag ${tag}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No tags yet. Tags will be auto-generated when the job description is saved, or you can add them manually above.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !canSave}
            title={hasChanges && !canSave ? 'Job title and company are required' : undefined}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
